import { useReverification, useSession, useUser } from "@clerk/clerk-react";
import { Button, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ProfileSectionTitle } from "./ProfileSectionTitle";

export function ActiveSessions() {
  const { user } = useUser();
  const { session: currentSession } = useSession();

  const [allSessions, setAllSessions] = useState<any[]>([]);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      user
        .getSessions()
        .then((fetchedSessions) => {
          setAllSessions(fetchedSessions);
        })
        .catch(console.error);
    }
  }, [user]);

  // The raw action that actually revokes sessions
  const revokeOtherSessions = async () => {
    if (!user || !currentSession) return;
    const otherSessions = allSessions.filter((s) => s.id !== currentSession.id);
    await Promise.all(otherSessions.map((s) => s.revoke()));

    // Refresh the session list
    const updatedSessions = await user.getSessions();
    setAllSessions(updatedSessions);
  };

  // Wrap it with Clerk's reverification hook
  const verifiedRevoke = useReverification(revokeOtherSessions);

  const handleRevokeOtherSessions = async () => {
    setRevoking(true);
    setRevokeError(null);
    try {
      await verifiedRevoke();
    } catch (error: any) {
      // Ignore user cancellation of the modal
      const isCancelled =
        error?.clerk_error === "reverification_cancelled" ||
        error?.code === "reverification_cancelled";
      if (!isCancelled) {
        console.error("Failed to revoke other sessions:", error);
        setRevokeError(
          error.errors?.[0]?.message || error.message || "Failed to sign out of other devices.",
        );
      }
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div>
      <ProfileSectionTitle>Active Sessions</ProfileSectionTitle>

      {revokeError && (
        <Typography sx={{ fontSize: "13px", color: "var(--color-good-wine)", mb: 2 }}>
          {revokeError}
        </Typography>
      )}

      <div className="flex flex-col gap-4 mb-4">
        {allSessions.map((sess) => {
          const isCurrent = sess.id === currentSession?.id;
          const device = sess.latestActivity?.deviceType || "Unknown device";
          const browser = sess.latestActivity?.browserName || "Unknown browser";
          const statusText = isCurrent ? "signed in now" : "active";

          return (
            <Typography key={sess.id} sx={{ fontSize: "13.5px", color: "var(--color-graphite)" }}>
              {isCurrent ? "This device" : device} • {browser} • {statusText}
            </Typography>
          );
        })}
      </div>
      <Button
        variant="outlined"
        onClick={handleRevokeOtherSessions}
        disabled={revoking || allSessions.length <= 1}
        sx={{
          color: "var(--color-rodeo-black)",
          borderColor: "var(--color-smoke)",
          textTransform: "uppercase",
          fontWeight: 700,
          fontSize: "11px",
          letterSpacing: "1.5px",
          padding: "10px 20px",
          "&:hover": {
            borderColor: "var(--color-graphite)",
            backgroundColor: "var(--color-background)",
          },
          "&.Mui-disabled": {
            backgroundColor: "var(--color-wax)",
          },
        }}
      >
        {revoking ? "Signing out..." : "Sign out of all other devices"}
      </Button>
    </div>
  );
}
